import { z } from 'zod'
import { TERRAINS, TERRAIN_MODES, type EffectTrigger, type SummonKind, type Terrain, type TerrainMode } from './card'

/**
 * Contratos do duelo: fases, acoes do jogador, eventos do motor e a visao filtrada.
 * Fontes da verdade: docs/01-regras-do-duelo.md e docs/05-protocolo-tempo-real.md
 */

// ---------------------------------------------------------------------------
// Fases e posicoes
// ---------------------------------------------------------------------------

/**
 * Ordem das fases do turno. docs/01-regras-do-duelo.md secao 2
 *
 * A ordem do array E a ordem do turno, e ha lugar que depende disso — a barra de fases da interface
 * deriva o progresso da posicao aqui.
 *
 * `STANDBY` nao faz nada hoje: existe para que uma carta possa menciona-la, como o manual preve, e
 * atravessa sozinha como a `DRAW`.
 */
export const PHASES = ['DRAW', 'STANDBY', 'MAIN_1', 'BATTLE', 'MAIN_2', 'END'] as const
export type Phase = (typeof PHASES)[number]

export const POSITIONS = ['ATTACK', 'DEFENSE'] as const
export type Position = (typeof POSITIONS)[number]

export const DUEL_STATUSES = ['ONGOING', 'FINISHED'] as const
export type DuelStatus = (typeof DUEL_STATUSES)[number]

/** `CARD_EFFECT`: vitoria declarada por uma carta (`WIN_DUEL`). docs/01-regras-do-duelo.md secao 10 */
export const DUEL_END_REASONS = [
  'LIFE_POINTS',
  'DECK_OUT',
  'SURRENDER',
  'TIMEOUT',
  'DISCONNECT',
  'DRAW',
  'CARD_EFFECT',
] as const
export type DuelEndReason = (typeof DUEL_END_REASONS)[number]

/** Resultado de uma batalha, do ponto de vista do atacante. docs/01-regras-do-duelo.md secao 5.2 */
export const BATTLE_OUTCOMES = [
  'DEFENDER_DESTROYED',
  'ATTACKER_DESTROYED',
  'BOTH_DESTROYED',
  'NO_DESTRUCTION',
  'DIRECT_HIT',
] as const
export type BattleOutcome = (typeof BATTLE_OUTCOMES)[number]

// ---------------------------------------------------------------------------
// Acoes do jogador. docs/05-protocolo-tempo-real.md secao 2.1
// ---------------------------------------------------------------------------

const zoneIndexSchema = z.number().int().min(0).max(4)

/**
 * Nao existe acao de compra: a compra e automatica na fase DRAW, executada pelo motor
 * ao iniciar o turno. docs/01-regras-do-duelo.md secao 2
 */
export const duelActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('SUMMON'),
    cardInstanceId: z.string().min(1),
    zone: zoneIndexSchema,
    position: z.enum(['ATTACK', 'FACE_DOWN_DEFENSE']),
    /**
     * Monstros proprios enviados ao cemiterio para pagar o Tributo.
     * docs/01-regras-do-duelo.md secao 4
     *
     * Ausente equivale a lista vazia — e o que mantem a acao valida para monstro de nivel baixo,
     * que e a maioria das invocacoes.
     */
    tributeInstanceIds: z.array(z.string().min(1)).optional(),
  }),
  z.object({ type: z.literal('CHANGE_POSITION'), cardInstanceId: z.string().min(1) }),
  z.object({ type: z.literal('SET_SPELL'), cardInstanceId: z.string().min(1), zone: zoneIndexSchema }),
  z.object({
    type: z.literal('ACTIVATE_SPELL'),
    cardInstanceId: z.string().min(1),
    /**
     * Zona de MONSTRO escolhida para uma Invocacao-Especial que o efeito faca.
     * docs/01-regras-do-duelo.md secao 4.2
     *
     * Ausente = a menor zona livre. E o mesmo criterio que vale no gatilho involuntario, onde o
     * jogador nao teve como escolher (docs/01 secao 6.6).
     */
    zone: zoneIndexSchema.optional(),
    targets: z.array(z.string().min(1)).optional(),
  }),
  /**
   * Ativacao MANUAL de uma Armadilha Normal ja baixada, em fase principal do proprio turno — a
   * segunda porta de ativacao da Armadilha (docs/01-regras-do-duelo.md secao 6). Continua e Contra
   * nao usam esta acao: a primeira ativa entrando em campo, a segunda so responde (secao 6.5).
   */
  z.object({
    type: z.literal('ACTIVATE_TRAP'),
    cardInstanceId: z.string().min(1),
    zone: zoneIndexSchema.optional(),
    targets: z.array(z.string().min(1)).optional(),
  }),
  /**
   * O jogador DECLARA o uso da habilidade especial de um monstro seu em campo — ou na mao, quando o custo e descartar
   * a propria carta (Thunder Dragon). docs/01-regras-do-duelo.md secao 6.1
   *
   * Nao se confunde com os gatilhos involuntarios (`ON_FLIP` e afins): ali o efeito acontece
   * sozinho, e o motor escolhe alvo no lugar do jogador (docs/01 secao 6.6). Aqui quem escolhe
   * e ele.
   */
  z.object({
    type: z.literal('ACTIVATE_MONSTER_EFFECT'),
    cardInstanceId: z.string().min(1),
    /**
     * O lado da moeda que o jogador CHAMA, quando a habilidade tem `COIN_FLIP`.
     * docs/01-regras-do-duelo.md secao 9
     */
    coinCall: z.enum(['HEADS', 'TAILS']).optional(),
    /** Como em `ACTIVATE_SPELL`: a zona da Invocacao-Especial, quando o efeito faz uma. */
    zone: zoneIndexSchema.optional(),
    targets: z.array(z.string().min(1)).optional(),
  }),
  /**
   * Invocacao-Ritual. docs/01-regras-do-duelo.md secao 4.3
   *
   * Mesma forma de `FUSION_SUMMON`, e pelo mesmo motivo: a jogada inteira e uma decisao so, e
   * parti-la em passos deixaria o estado no meio do caminho se o segundo falhasse.
   */
  z.object({
    type: z.literal('RITUAL_SUMMON'),
    /** A Magia de Ritual, na mao. */
    spellInstanceId: z.string().min(1),
    /** O monstro de Ritual, na mao. */
    monsterInstanceId: z.string().min(1),
    /** Os monstros do PROPRIO campo que pagam o Tributo. A soma dos niveis e o custo. */
    tributeInstanceIds: z.array(z.string().min(1)).min(1),
    zone: zoneIndexSchema,
    position: z.enum(POSITIONS),
  }),
  z.object({
    type: z.literal('FUSION_SUMMON'),
    fusionCardInstanceId: z.string().min(1),
    materialInstanceIds: z.array(z.string().min(1)).min(2),
    zone: zoneIndexSchema,
    position: z.enum(POSITIONS),
  }),
  /**
   * A Invocacao pelo procedimento proprio (`summonProcedure`): do monstro na mao (Larvae Moth, Great Moth) ou, com
   * `fromExtraDeck`, no Extra Deck (Gate Guardian, regra da casa). docs/01-regras-do-duelo.md secao 4.6, ADR 0053
   *
   * Invocacao-Especial: nao exige nem consome a Invocacao-Normal. Os Tributos sao monstros do PROPRIO campo, virados
   * inclusive, e fecham o procedimento exatamente. Mesma forma da Fusao e do Ritual: a jogada inteira e uma decisao so.
   */
  z.object({
    type: z.literal('PROCEDURE_SUMMON'),
    cardInstanceId: z.string().min(1),
    tributeInstanceIds: z.array(z.string().min(1)).min(1),
    zone: zoneIndexSchema,
    position: z.enum(POSITIONS),
  }),
  z.object({
    type: z.literal('ATTACK'),
    attackerInstanceId: z.string().min(1),
    /** Ausente = ataque direto. docs/01-regras-do-duelo.md secao 5.1 */
    targetInstanceId: z.string().min(1).optional(),
    /**
     * O lado da moeda que o jogador CHAMA, quando o atacante lanca moeda com chamada ao declarar
     * (`ON_THIS_CARD_ATTACKS`, Jirai Gumo). docs/01-regras-do-duelo.md secoes 5.1 e 9
     */
    coinCall: z.enum(['HEADS', 'TAILS']).optional(),
  }),
  z.object({ type: z.literal('ADVANCE_PHASE') }),
  z.object({ type: z.literal('END_TURN'), discardInstanceIds: z.array(z.string().min(1)).optional() }),
  /**
   * Resposta a uma janela de corrente. Sem `cardInstanceId` significa passar.
   * docs/01-regras-do-duelo.md secao 6.4
   */
  z.object({
    type: z.literal('RESPOND'),
    windowId: z.string().min(1),
    cardInstanceId: z.string().min(1).optional(),
    targets: z.array(z.string().min(1)).optional(),
  }),
  /**
   * Os alvos que o JOGADOR escolheu para um efeito de virada. docs/01-regras-do-duelo.md secao 6.6
   *
   * Vem na ordem dos passos que a carta declara (custo primeiro, depois as acoes), e o motor valida
   * cada um contra os candidatos da propria pendencia — o cliente nunca decide legalidade.
   *
   * `targets: []` e a forma valida de dizer "resolva pelo criterio automatico": e o que o prazo
   * estourado usa, e o que impede um cliente sem a tela de escolha de travar o duelo.
   */
  z.object({
    type: z.literal('CHOOSE_TARGETS'),
    choiceId: z.string().min(1),
    targets: z.array(z.string().min(1)),
  }),
  z.object({ type: z.literal('SURRENDER') }),
  /**
   * Liga ou desliga as respostas livres de quem manda: desligadas, a janela dele nao abre quando todas as cartas
   * elegiveis sao livres (a Continua e a segunda porta). Aceita a qualquer momento, dos dois lados, sem evento.
   * docs/01-regras-do-duelo.md secao 6.4, docs/backlog/165
   */
  z.object({ type: z.literal('SET_FREE_RESPONSES'), enabled: z.boolean() }),
])
export type DuelAction = z.infer<typeof duelActionSchema>
export type DuelActionType = DuelAction['type']

// ---------------------------------------------------------------------------
// Acao roteirizada. docs/20-duelo-guiado.md secao 4.2
// ---------------------------------------------------------------------------

/**
 * De onde a carta que o roteiro nomeia e lida, na visao de QUEM executa o passo.
 * docs/20-duelo-guiado.md secao 4.2
 *
 * Sao tres formas porque duas perguntas diferentes precisam de resposta: "a carta que eu SEI qual
 * e" e "a carta que esta NAQUELE LUGAR". A segunda existe por causa de docs/05 secao 4 — o
 * `cardId` de carta virada alheia nao chega a ninguem, mas a zona e publica nas tres visoes.
 */
export const scriptedCardRefSchema = z.discriminatedUnion('from', [
  /** Minha mao. Resolve contra `PlayerView.you.hand`, que so existe para o dono. */
  z.object({ from: z.literal('HAND'), cardId: z.string().min(1) }),
  /** Meu Extra Deck. Resolve contra `PlayerView.you.extraDeck`, idem (docs/05 secao 3.0.1). */
  z.object({ from: z.literal('EXTRA'), cardId: z.string().min(1) }),
  /**
   * Uma zona do tabuleiro. E a UNICA forma de o roteiro apontar carta do outro lado: `side` e
   * relativo a quem executa, entao o mesmo dado lido do assento do jogador significa "meu lado" e
   * lido do assento roteirizado significa "o lado dele".
   */
  z.object({
    from: z.literal('ZONE'),
    side: z.enum(['SELF', 'OPPONENT']),
    row: z.enum(['MONSTER', 'SPELL', 'FIELD']),
    zone: zoneIndexSchema,
  }),
])
export type ScriptedCardRef = z.infer<typeof scriptedCardRefSchema>

/**
 * Uma jogada dita ANTES de o duelo existir. docs/20-duelo-guiado.md secao 4.2
 *
 * A diferenca para `DuelAction` e uma so, e e ela que define o tipo: aquela fala em
 * `cardInstanceId`, que nasce com o duelo; esta fala em `cardId` ou em zona, porque o roteiro de
 * um capitulo e escrito meses antes. Quem traduz uma na outra e `resolveScriptedAction`, no motor.
 *
 * `RITUAL_SUMMON` e `SURRENDER` ficam de FORA de proposito: o Ritual esta fora do escopo do Duelo
 * Guiado (docs/20 secao 3.1), e desistencia nao se roteiriza — um adversario que desiste por
 * escrito no meio da aula e um fim de duelo que o jogador nao entende.
 */
export const scriptedActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('SUMMON'),
    card: scriptedCardRefSchema,
    zone: zoneIndexSchema,
    position: z.enum(['ATTACK', 'FACE_DOWN_DEFENSE']),
    /** Os Tributos ESCOLHIDOS pelo roteiro (docs/01 secao 4). Vazio = nivel que nao custa. */
    tributes: z.array(scriptedCardRefSchema).default([]),
  }),
  z.object({ type: z.literal('CHANGE_POSITION'), card: scriptedCardRefSchema }),
  z.object({ type: z.literal('SET_SPELL'), card: scriptedCardRefSchema, zone: zoneIndexSchema }),
  z.object({
    type: z.literal('ACTIVATE_SPELL'),
    card: scriptedCardRefSchema,
    /** Zona de monstro para uma Invocacao-Especial que o efeito faca (docs/01 secao 4.2). */
    zone: zoneIndexSchema.optional(),
    targets: z.array(scriptedCardRefSchema).default([]),
  }),
  z.object({
    type: z.literal('ACTIVATE_MONSTER_EFFECT'),
    card: scriptedCardRefSchema,
    coinCall: z.enum(['HEADS', 'TAILS']).optional(),
    zone: zoneIndexSchema.optional(),
    targets: z.array(scriptedCardRefSchema).default([]),
  }),
  z.object({
    type: z.literal('FUSION_SUMMON'),
    fusion: scriptedCardRefSchema,
    materials: z.array(scriptedCardRefSchema).min(2),
    zone: zoneIndexSchema,
    position: z.enum(POSITIONS),
  }),
  /** `target: null` = ataque direto. docs/01 secao 5.1 */
  z.object({
    type: z.literal('ATTACK'),
    attacker: scriptedCardRefSchema,
    target: scriptedCardRefSchema.nullable().default(null),
    coinCall: z.enum(['HEADS', 'TAILS']).optional(),
  }),
  z.object({ type: z.literal('ADVANCE_PHASE') }),
  z.object({ type: z.literal('END_TURN'), discard: z.array(scriptedCardRefSchema).default([]) }),
  /**
   * `card: null` = passar a janela. O `windowId` NAO e campo do roteiro: ele nasce em tempo de
   * duelo, e quem o le e o resolvedor, do `pendingResponse` da visao.
   */
  z.object({
    type: z.literal('RESPOND'),
    card: scriptedCardRefSchema.nullable().default(null),
    targets: z.array(scriptedCardRefSchema).default([]),
  }),
  /**
   * Os alvos do efeito de virada, no roteiro. O `choiceId` NAO e campo do roteiro, pelo mesmo
   * motivo do `windowId` do `RESPOND`: ele nasce em tempo de duelo, e o resolvedor o le da
   * pendencia da visao. Lista vazia = criterio automatico (docs/01 secao 6.6).
   */
  z.object({
    type: z.literal('CHOOSE_TARGETS'),
    targets: z.array(scriptedCardRefSchema).default([]),
  }),
])
export type ScriptedAction = z.infer<typeof scriptedActionSchema>
export type ScriptedActionType = ScriptedAction['type']

// ---------------------------------------------------------------------------
// Eventos do motor. docs/05-protocolo-tempo-real.md secao 3.1
// ---------------------------------------------------------------------------

export const DUEL_EVENT_TYPES = [
  'DUEL_STARTED',
  'TURN_STARTED',
  'PHASE_CHANGED',
  'CARD_DRAWN',
  'MONSTER_SUMMONED',
  'MONSTER_SET',
  'POSITION_CHANGED',
  'FLIP_SUMMONED',
  'SPELL_SET',
  'SPELL_ACTIVATED',
  'MONSTER_EFFECT_ACTIVATED',
  'EFFECT_RESOLVED',
  'EFFECT_FIZZLED',
  'EFFECT_CARDS_REVEALED',
  'SPELL_REVEALED',
  'ATTACK_DECLARED',
  'ATTACK_NEGATED',
  'CARD_NEGATED',
  'SUMMON_NEGATED',
  'BATTLE_RESOLVED',
  'MONSTER_DESTROYED',
  'MONSTER_RETURNED_TO_HAND',
  'CARD_SENT_TO_GRAVEYARD',
  'CARD_DISCARDED',
  'CARD_BANISHED',
  'CARD_ADDED_TO_HAND',
  'CARD_RETURNED_TO_DECK',
  'CARD_RETURNED_TO_EXTRA_DECK',
  'TERRAIN_REROLLED',
  'LP_CHANGED',
  'FIELD_CHANGED',
  'FUSION_SUMMONED',
  'SPECIAL_SUMMONED',
  'RITUAL_SUMMONED',
  'PROCEDURE_SUMMONED',
  'TURN_COUNT_ADVANCED',
  'CONTROL_TAKEN',
  'CONTROL_RETURNED',
  'CONTROL_GIVEN',
  'COIN_FLIPPED',
  'ATK_DEF_MODIFIERS_REVERSED',
  'MANDATORY_TRIBUTE_GRANTED',
  'TRAP_DESTRUCTION_REPLACED',
  'DECK_SHUFFLED',
  'CARDS_RETURNED_TO_DECK_BOTTOM',
  'TURN_ENDED',
  'DUEL_ENDED',
] as const
export type DuelEventType = (typeof DUEL_EVENT_TYPES)[number]

/**
 * Eventos sao a UNICA fonte de animacao do frontend (docs/08-frontend.md secao 4).
 *
 * Regra de vazamento: um evento NUNCA carrega `cardId` de carta que continua oculta.
 * CARD_DRAWN e MONSTER_SET, por exemplo, so trazem `instanceId`.
 */
export type DuelEvent =
  | { type: 'DUEL_STARTED'; startingPlayerId: string; terrains: Terrain[] }
  | { type: 'TURN_STARTED'; playerId: string; turn: number }
  | { type: 'PHASE_CHANGED'; phase: Phase }
  | { type: 'CARD_DRAWN'; playerId: string; instanceId: string }
  | { type: 'MONSTER_SUMMONED'; playerId: string; instanceId: string; cardId: string; zone: number; position: Position }
  | { type: 'MONSTER_SET'; playerId: string; instanceId: string; zone: number }
  | { type: 'POSITION_CHANGED'; instanceId: string; position: Position; faceDown: boolean }
  | { type: 'FLIP_SUMMONED'; instanceId: string; cardId: string }
  | { type: 'SPELL_SET'; playerId: string; instanceId: string; zone: number }
  /**
   * `fromSet` diz se a carta estava BAIXADA na zona e virou ao ativar (a armadilha disparando, a magia
   * baixada), ou se veio da mao. A tela anima as duas de jeitos diferentes, e nao pode descobrir qual
   * foi comparando estados: o `STATE_SYNC` da leva ja chega com a carta de face para cima.
   *
   * `zone` e a zona de magia de `playerId` em que a carta fica enquanto resolve (docs/01 secao 6): a
   * propria, se baixada; a menor livre, se da mao. `null` quando ela nao passa por zona de magia (a Magia
   * de Campo, o monstro que responde da mao). A Magia Normal resolve na mesma leva, e o estado ja chega
   * com ela no cemiterio. docs/05-protocolo-tempo-real.md secao 3.1
   */
  | {
      type: 'SPELL_ACTIVATED'
      playerId: string
      instanceId: string
      cardId: string
      fromSet: boolean
      zone: number | null
    }
  /**
   * Habilidade de monstro ativada — declarada (ACTIVATE_MONSTER_EFFECT) ou de virada (ON_FLIP).
   * Mesmo shape de SPELL_ACTIVATED, sempre emitido antes de EFFECT_RESOLVED/EFFECT_FIZZLED do
   * mesmo efeito. docs/backlog/59-o-aviso-de-quem-ativou.md
   */
  /**
   * `spellZone` so na habilidade da mao que equipa a propria carta (`EQUIP_THIS_CARD`, Cocoon of Evolution): a zona de
   * magia para onde a carta saiu da mao. docs/01-regras-do-duelo.md secao 6.1, ADR 0053
   */
  | {
      type: 'MONSTER_EFFECT_ACTIVATED'
      playerId: string
      instanceId: string
      cardId: string
      spellZone?: number
    }
  | { type: 'EFFECT_RESOLVED'; instanceId: string; op: string }
  /**
   * `blockedBy` — o `instanceId` da carta que IMPEDIU o alvo (ex.: `PREVENT_TARGETING`, docs/01
   * secao 6.0), quando a razao do fizzle e essa. `reason` continua sendo string livre de log, e
   * cobre TODO motivo de fizzle (alvo ja saiu de campo, contagem insuficiente, valor nao
   * declarado, etc); `blockedBy` so vem preenchido nos casos em que existe uma carta especifica
   * a apontar — a tela usa a presenca dele pra saber quando destacar quem bloqueou, e nao tenta
   * adivinhar a partir de `reason`. docs/05-protocolo-tempo-real.md secao 3.1
   */
  | { type: 'EFFECT_FIZZLED'; instanceId: string; op: string; reason: string; blockedBy?: string }
  /**
   * `REVEAL_CARDS` olhou o topo do Deck de algum jogador (docs/02-catalogo-de-cartas.md secao 3.3).
   *
   * PRIVADO POR DESTINATARIO, como `SPELL_REVEALED` logo abaixo (docs/05 secao 4.1): `cardId` de
   * cada carta chega preenchido so para `toPlayerId` — quem ativou o efeito, e nao necessariamente
   * o dono do Deck revelado (Telescopio Antigo olha o Deck do OPONENTE). Para todos os outros —
   * dono do Deck incluso, e o espectador — cada `cardId` vem `null`. Quem filtra e `projectEvents`
   * no motor, e nunca o cliente.
   */
  | {
      type: 'EFFECT_CARDS_REVEALED'
      sourceCardId: string
      toPlayerId: string
      cards: { instanceId: string; cardId: string | null }[]
      /**
       * As cartas sairam de um DECK, que e oculto ate na ordem (docs/05 secao 4.2): quem nao e o destinatario recebe so a
       * contagem, sem `instanceId` — senao a compra seguinte diria quais eram, e em que ordem (Big Eye).
       */
      fromDeck?: true
      /**
       * Marca a revelacao como PUBLICA, mesmo padrao de `SPELL_REVEALED.public` (De-Spell,
       * docs/02 secao 3.3): todo mundo recebe o evento com `cardId`, inclusive quem nao olhou.
       * `REVEAL_CARDS` com `source: 'ENTIRE_FIELD'` (The Stern Mystic, leva_sdk_sdy/curadoria)
       * sempre emite `public: true` — o campo inteiro nao tem "quem olhou", os dois lados veem.
       * Ausente (ou `false`) e o comportamento de sempre: privado por destinatario, filtrado em
       * `projectEvents`. docs/05 secao 4.1
       */
      public?: boolean
    }
  /**
   * Uma carta VIRADA da zona de magia foi olhada por um efeito, sem ser ativada.
   * docs/01-regras-do-duelo.md secao 6
   *
   * PRIVADO POR DESTINATARIO por padrao (docs/05 secao 4.1): `cardId` chega preenchido so para
   * `toPlayerId`, quem virou a carta. Para todos os outros — o dono, e o espectador — ele vem
   * `null`. Quem filtra e `projectEvents` no motor, e nunca o cliente.
   *
   * `public: true` e a excecao (`revealPublicly` em `DESTROY_SPELL_TRAP`, docs/02 secao 3.3):
   * De-Spell revela pros DOIS lados quando erra o alvo, ao contrario do precedente privado de
   * `base-213`/`base-267`. Com `public`, `cardId` chega preenchido pra TODO mundo, e `toPlayerId`
   * continua marcando quem causou a revelacao — narrativo, nao mais um filtro. Ausente (ou `false`)
   * mantem o comportamento privado de sempre.
   */
  | {
      type: 'SPELL_REVEALED'
      playerId: string
      instanceId: string
      toPlayerId: string
      cardId: string | null
      public?: boolean
    }
  | { type: 'ATTACK_DECLARED'; attackerInstanceId: string; targetInstanceId: string | null }
  /**
   * `negatedBy` e o `instanceId` de quem tinha o `NEGATE_ATTACK` — sempre presente, ao contrario de
   * `EFFECT_FIZZLED.blockedBy`: aqui so existe UM jeito de o evento acontecer, e a carta causadora
   * e sempre conhecida (e ja publica: ela ativou pra chegar ate aqui). docs/05-protocolo-tempo-real.md
   * secao 4.2
   */
  | { type: 'ATTACK_NEGATED'; attackerInstanceId: string; negatedBy: string }
  /**
   * Um elo da corrente foi anulado por uma Armadilha Contra. docs/01-regras-do-duelo.md secao 6.5
   *
   * Existe porque um elo que simplesmente some da sequencia e indistinguivel de um defeito: a tela
   * precisa poder mostrar que a carta foi anulada, e nao que ela nunca aconteceu. `negatedBy` e o
   * `instanceId` da Armadilha Contra — sempre presente, pelo mesmo motivo de `ATTACK_NEGATED.negatedBy`
   * (docs/05-protocolo-tempo-real.md secao 3.1).
   */
  | { type: 'CARD_NEGATED'; instanceId: string; cardId: string; negatedBy: string }
  /**
   * A Invocacao do monstro foi negada na janela da tentativa (`NEGATE_SUMMON`). docs/01-regras-do-duelo.md secao 4.5
   *
   * Nao e `CARD_NEGATED`: la some um ELO da corrente, e aqui nao ha elo — a Invocacao nunca foi um. `negatedBy` e a
   * Armadilha Contra, sempre presente. O `MONSTER_DESTROYED` e a chegada ao cemiterio do mesmo monstro vem logo depois.
   * docs/05-protocolo-tempo-real.md secao 3.1
   */
  | { type: 'SUMMON_NEGATED'; instanceId: string; cardId: string; negatedBy: string }
  | {
      type: 'BATTLE_RESOLVED'
      attackerInstanceId: string
      targetInstanceId: string | null
      attackerAtk: number
      defenderValue: number | null
      outcome: BattleOutcome
    }
  | { type: 'MONSTER_DESTROYED'; instanceId: string; cardId: string }
  | { type: 'MONSTER_RETURNED_TO_HAND'; playerId: string; instanceId: string; cardId: string }
  | { type: 'CARD_SENT_TO_GRAVEYARD'; playerId: string; instanceId: string; cardId: string }
  | { type: 'CARD_DISCARDED'; playerId: string; instanceId: string; cardId: string }
  /** Saiu do cemiterio para "removido do jogo". docs/01-regras-do-duelo.md secao 3.0 */
  | { type: 'CARD_BANISHED'; playerId: string; instanceId: string; cardId: string }
  /**
   * Para a mao do dono (`ADD_TO_HAND`): do cemiterio, ou do proprio Deck com `fromDeck` (a busca). Publico nas tres
   * visoes, com o `cardId`, nos dois casos. docs/05 secao 3.1
   */
  | { type: 'CARD_ADDED_TO_HAND'; playerId: string; instanceId: string; cardId: string; fromDeck?: true }
  /** Do cemiterio para o TOPO do Deck do dono (`RETURN_TO_DECK_TOP`). Publico, com o `cardId`. docs/05 secao 3.1 */
  | { type: 'CARD_RETURNED_TO_DECK'; playerId: string; instanceId: string; cardId: string }
  /**
   * O monstro do Extra Deck (`isExtraDeckMonster`: o de Fusao e o com `fromExtraDeck`) que iria para a mao ou o Deck
   * volta ao Extra Deck do dono: do campo (`RETURN_TO_HAND`) ou do cemiterio. Publico, com o `cardId`: a carta saiu de
   * zona publica, e o Extra Deck alheio so mostra a contagem. docs/01-regras-do-duelo.md secoes 4.6 e 7, docs/05 secao
   * 3.1
   */
  | {
      type: 'CARD_RETURNED_TO_EXTRA_DECK'
      playerId: string
      instanceId: string
      cardId: string
      from: 'FIELD' | 'GRAVEYARD'
    }
  | { type: 'LP_CHANGED'; playerId: string; delta: number; lifePoints: number }
  | { type: 'FIELD_CHANGED'; terrain: Terrain | null }
  /** O Deck do jogador foi embaralhado por efeito. Publico: nada da ordem nova. docs/05 secao 3.1 */
  | { type: 'DECK_SHUFFLED'; playerId: string }
  /**
   * `count` cartas da MAO daquele jogador foram para o FUNDO do Deck dele (`RETURN_TO_DECK_BOTTOM`, Yado Karu). Igual
   * nas tres visoes, e por isso sem `cardId` nem `instanceId`: a mao e oculta, e cada carta ganha `instanceId` novo ao
   * entrar no Deck. docs/05 secao 3.1
   */
  | { type: 'CARDS_RETURNED_TO_DECK_BOTTOM'; playerId: string; count: number }
  /**
   * O mapa novo da rodada, no modo de terreno FUN. docs/01-regras-do-duelo.md secao 3
   *
   * SEPARADO de `FIELD_CHANGED` de proposito: os dois anunciam que o terreno vigente mudou, e as
   * causas sao opostas — ali e a carta que alguem jogou, aqui e a rodada que virou. A tela anima a
   * partir dos eventos (docs/08 secao 4) e precisa das duas animacoes; reusar um evento para as duas
   * causas obrigaria o cliente a inferir qual foi pelo estado.
   *
   * Carrega o mapa INTEIRO, e nao a diferenca: sao cinco posicoes, a mensagem e rara (uma por
   * rodada, e so num dos modos), e um diff obrigaria o cliente a manter uma segunda copia do estado
   * para aplica-lo.
   */
  | { type: 'TERRAIN_REROLLED'; terrains: Terrain[] }
  | { type: 'FUSION_SUMMONED'; playerId: string; instanceId: string; cardId: string; zone: number; position: Position }
  /** Invocacao-Ritual. docs/01-regras-do-duelo.md secao 4.3 */
  | {
      type: 'RITUAL_SUMMONED'
      playerId: string
      instanceId: string
      cardId: string
      zone: number
      position: Position
    }
  /**
   * Invocacao pelo procedimento proprio (`PROCEDURE_SUMMON`, Gate Guardian). docs/01-regras-do-duelo.md secao 4.6
   *
   * Publico nas tres visoes, como os outros eventos de Invocacao. Sai depois das chegadas dos Tributos ao cemiterio e
   * antes da janela da tentativa; negada a Invocacao, vem o `SUMMON_NEGATED` de sempre. docs/05 secao 3.1, ADR 0053
   */
  | {
      type: 'PROCEDURE_SUMMONED'
      playerId: string
      instanceId: string
      cardId: string
      zone: number
      position: Position
    }
  /**
   * O contador de turnos do procedimento andou +1 na Espera de `playerId`, quem controla a carta (o Casulo de Evolucao na
   * zona de magia, docs/01-regras-do-duelo.md secao 4.6, ADR 0053). PUBLICO nas tres visoes: o Casulo esta face-up, e os
   * turnos foram a vista. So o contador do Casulo o emite, por enquanto; o de Esperas (Castelo, Pumpking), nao.
   */
  | {
      type: 'TURN_COUNT_ADVANCED'
      playerId: string
      instanceId: string
      cardId: string
      count: number
      max: number
    }
  /**
   * Invocacao-Especial por efeito de carta. docs/01-regras-do-duelo.md secao 4.2
   *
   * Evento proprio, e nao `MONSTER_SUMMONED`: o monstro nao veio da mao como jogada do turno, e a
   * tela conta as duas coisas de formas diferentes. Nem `FUSION_SUMMONED`, que e de fusao.
   */
  | {
      type: 'SPECIAL_SUMMONED'
      playerId: string
      instanceId: string
      cardId: string
      zone: number
      position: Position
    }
  /**
   * Controle de um monstro tomado por efeito de carta (`TAKE_CONTROL`).
   * docs/01-regras-do-duelo.md secao 4.2, docs/02-catalogo-de-cartas.md secao 3.3
   *
   * `playerId` e o novo controlador. PUBLICO como qualquer outro movimento de campo — as duas
   * partes sabem quem controla o que, nao e informacao oculta (docs/05 secao 4).
   *
   * `fromZone` e a zona de monstro de onde a carta saiu, no campo do outro jogador: a tela anima a
   * viagem de la ate `zone`, e nao pode descobrir a origem pelo estado (docs/05 secao 3.1).
   */
  | { type: 'CONTROL_TAKEN'; playerId: string; instanceId: string; cardId: string; zone: number; fromZone: number }
  /**
   * O controle tomado por `TAKE_CONTROL` expirou e o monstro voltou ao campo do DONO.
   * docs/01-regras-do-duelo.md secao 4.2
   *
   * So dispara quando ha zona livre no campo do dono — sem zona, a carta vai ao Cemiterio dele e
   * o evento e o de sempre, `CARD_SENT_TO_GRAVEYARD`, sem evento proprio: nao e um novo tipo de
   * movimento de carta, e o cemiterio ja tem o evento que basta.
   */
  | { type: 'CONTROL_RETURNED'; playerId: string; instanceId: string; cardId: string; zone: number; fromZone: number }
  /**
   * O monstro foi DADO, para sempre, ao oponente de `playerId` (`GIVE_CONTROL`, Mushroom Man #2). docs/01 secao 4.2,
   * docs/05 secao 3.1, ADR 0052
   *
   * Ao contrario de `CONTROL_TAKEN`/`CONTROL_RETURNED`, `playerId` e QUEM DEU: `fromZone` e a zona dele, e `zone` a de
   * destino, no campo do oponente. Publico nas tres visoes: a carta estava face-up e a habilidade foi a vista.
   */
  | { type: 'CONTROL_GIVEN'; playerId: string; instanceId: string; cardId: string; zone: number; fromZone: number }
  /**
   * A moeda foi lancada. docs/01-regras-do-duelo.md secao 9
   *
   * PUBLICO, e com o numero sorteado dentro: e o numero que torna o resultado auditável — sem ele,
   * a moeda seria um oraculo. `draw` par e cara, impar e coroa.
   *
   * Um evento por moeda. `call` e `won` so existem quando a carta deixa chamar: na que so conta caras (Barrel Dragon),
   * os dois faltam, e "ganhou" a cada cara seria falso. docs/01 secao 6.2
   */
  | {
      type: 'COIN_FLIPPED'
      playerId: string
      call?: 'HEADS' | 'TAILS'
      draw: number
      result: 'HEADS' | 'TAILS'
      won?: boolean
    }
  /**
   * `REVERSE_ATK_DEF_MODIFIERS` (Reverse Trap) ligou a flag global.
   * docs/01-regras-do-duelo.md secao 3.4, docs/02-catalogo-de-cartas.md secao 3.3
   *
   * PUBLICO: a inversao vale para os dois jogadores, e nenhum dos dois tem motivo pra nao saber
   * que ela esta valendo.
   */
  | { type: 'ATK_DEF_MODIFIERS_REVERSED'; sourceInstanceId: string }
  /**
   * `GRANT_MANDATORY_TRIBUTE` (Soul Exchange) resolveu: `instanceId` (do OPONENTE de quem ativou,
   * `playerId`) vira Tributo obrigatorio pelo resto do turno, e a Fase de Batalha do turno some
   * junto — as duas consequencias da mesma ativacao (§4, §5.1).
   * docs/01-regras-do-duelo.md secao 4, docs/02-catalogo-de-cartas.md secao 3.3
   *
   * PUBLICO: a restricao e sobre uma jogada, nao sobre carta oculta — os dois lados sabem que ela
   * esta valendo, como `CONTROL_TAKEN`/`ATK_DEF_MODIFIERS_REVERSED`.
   */
  | { type: 'MANDATORY_TRIBUTE_GRANTED'; sourceInstanceId: string; playerId: string; instanceId: string }
  /**
   * A Fake Trap de `playerId` substituiu a destruicao das Armadilhas dele num passo de um elo do oponente.
   * docs/01-regras-do-duelo.md secao 6.5, docs/05-protocolo-tempo-real.md secao 3.1
   *
   * PUBLICO, menos `faceDownInstanceIds`: as poupadas viradas so vao ao dono (`projectEvents`); o oponente e o
   * espectador recebem a lista vazia, e o evento nao lhes diz quais cartas viradas eram Armadilha.
   */
  | {
      type: 'TRAP_DESTRUCTION_REPLACED'
      playerId: string
      sourceInstanceId: string
      sourceCardId: string
      instanceIds: string[]
      faceDownInstanceIds: string[]
    }
  | { type: 'TURN_ENDED'; playerId: string }
  | { type: 'DUEL_ENDED'; winnerId: string | null; reason: DuelEndReason }

// ---------------------------------------------------------------------------
// Visao filtrada (fog of war). docs/05-protocolo-tempo-real.md secao 4
// ---------------------------------------------------------------------------

/**
 * Monstro como visto por um jogador.
 * Campos `cardId`, `atk`, `def`, `effectiveAtk` e `effectiveDef` sao `null` quando a carta esta
 * oculta para quem recebe a visao. O terreno e sempre publico.
 */
export type MonsterView = {
  instanceId: string
  zone: number
  terrain: Terrain
  position: Position
  faceDown: boolean
  cardId: string | null
  /**
   * A versao da carta (ADR 0061): a arte e o acabamento da copia. Anexada pela sala onde, e so onde, o `cardId` vem
   * preenchido (docs/05 secao 4); ausente na versao de casa.
   */
  versionId?: string
  name: string | null
  atk: number | null
  def: number | null
  effectiveAtk: number | null
  effectiveDef: number | null
  hasAttacked: boolean
  summonedThisTurn: boolean
  positionChangedThisTurn: boolean
  /** Ja usou a habilidade especial neste turno. docs/01-regras-do-duelo.md secao 6.1 */
  hasActivatedEffect: boolean
  /**
   * Ja gastou o efeito de uso unico "enquanto face-up" (`limit: ONCE_WHILE_FACE_UP`, Sanga of the Thunder).
   * docs/01-regras-do-duelo.md secao 6.4. Publico nas tres visoes: o monstro e o uso estao a vista. Opcional, e so
   * vem quando e `true`, pelo mesmo motivo de `extraNormalSummonUsedThisTurn`.
   */
  faceUpEffectUsed?: true
  /**
   * Quem invocou este monstro por Virada (The Immortal of Thunder perde PV ao ir ao cemiterio so assim).
   * docs/01-regras-do-duelo.md secao 6.6. Publico: a Invocacao por Virada foi a vista.
   */
  flipSummonedBy?: string
  /**
   * ATK e DEF ORIGINAIS trocados ate o fim do turno (`SWAP_ORIGINAL_ATK_DEF`, Shield & Sword).
   * docs/01-regras-do-duelo.md secao 3.4. Publico: so alcanca monstro face-up, e a ativacao foi a vista. `atk`/`def`
   * continuam os impressos; `effectiveAtk`/`effectiveDef` ja chegam trocados. So vem quando e `true`.
   */
  originalAtkDefSwapped?: true
  /**
   * O monstro esta neste campo por controle EMPRESTADO ate o fim do turno (`TAKE_CONTROL`), e volta
   * ao dono quando o turno acaba. docs/05-protocolo-tempo-real.md secao 4, docs/01 secao 4.2
   *
   * Publico nas tres visoes: a troca de campo aconteceu a vista, e quem olha a mesa sabe de quem e o
   * monstro. Opcional pelo mesmo motivo de `extraNormalSummonUsedThisTurn`: so vem quando e `true`,
   * e um mock de frontend sem a carta no jogo segue sem o campo.
   */
  borrowed?: boolean
  /**
   * O DONO do monstro, so quando ele nao e quem o controla: o revivido do cemiterio do oponente, o emprestado, o dado.
   * docs/05-protocolo-tempo-real.md secao 4, docs/01-regras-do-duelo.md secao 6.1
   *
   * Publico nas tres visoes: a Invocacao do cemiterio alheio e a troca de campo foram a vista. E o que deixa a enumeracao
   * do Casulo de Evolucao em campo (so o do proprio dono se equipa) chegar a mesma lista que o reducer. ADR 0053 secao 9
   */
  ownerId?: string
  /**
   * As marcas adiadas que o monstro carrega (Electric Lizard, Steel Scorpion). docs/01-regras-do-duelo.md secao 6.3
   *
   * Publicas nas tres visoes: o ataque e o aviso de ativacao foram a vista (docs/05 secao 4). Opcional, e so vem com
   * pelo menos uma marca, pelo mesmo motivo de `borrowed`.
   */
  delayedMarks?: DelayedMarkView[]
  /**
   * O contador de Esperas do monstro (Castle of Dark Illusions, Pumpking). docs/01-regras-do-duelo.md secao 6.0, item 8
   *
   * Publico nas tres visoes: a virada, as Esperas e o ATK que ele produz foram a vista (docs/05 secao 4). Opcional, e so
   * vem quando o monstro tem um.
   */
  turnCounter?: TurnCounterView
  /**
   * A marca de Invocacao correta (`properlySummoned`): o monstro com `noNormalSummon` entrou pelo procedimento dele.
   * docs/01-regras-do-duelo.md secao 4.6, ADR 0053
   *
   * Publica nas tres visoes: a Invocacao foi a vista (docs/05 secao 4). So no monstro visivel a quem recebe a visao — no
   * virado, diria qual carta e. Opcional, e so vem quando e `true`.
   */
  properlySummoned?: true
}

/**
 * O contador de Esperas como a visao o mostra. `count` sao as Esperas ja contadas, de 0 ao teto `max`: a mesa conta para
 * cima ("x0", "x1"…), e a ficha mostra o total ("x2 de 4"). docs/01-regras-do-duelo.md secao 6.0, item 8
 */
export type TurnCounterView = {
  count: number
  max: number
}

/**
 * O que a marca adiada faz quando vence. docs/01-regras-do-duelo.md secao 6.3
 *
 * `PREVENT_ATTACK`: o monstro nao declara ataque no turno do vencimento. `DESTROY`: e destruido na Fase Final dele.
 */
export const DELAYED_MARK_KINDS = ['PREVENT_ATTACK', 'DESTROY'] as const
export type DelayedMarkKind = (typeof DELAYED_MARK_KINDS)[number]

/**
 * Uma marca adiada como a visao a mostra. docs/01-regras-do-duelo.md secao 6.3
 *
 * `turnsLeft` conta os turnos de `playerId` (quem declarou o ataque) que ainda vao comecar ate o do vencimento: com 0, o
 * turno do vencimento e o atual. `turns` e o prazo inteiro: a mesa conta para cima, `turns - turnsLeft` turnos ja
 * passados, como o relogio dos jogos oficiais (decisao do Paulo, 17/10/2026). `sourceCardId` e a carta que gravou a
 * marca, para a ficha dizer de onde ela veio.
 */
export type DelayedMarkView = {
  kind: DelayedMarkKind
  playerId: string
  turnsLeft: number
  turns: number
  sourceCardId: string
}

export type SpellView = {
  instanceId: string
  zone: number
  faceDown: boolean
  cardId: string | null
  /**
   * A versao da carta (ADR 0061): a arte e o acabamento da copia. Anexada pela sala onde, e so onde, o `cardId` vem
   * preenchido (docs/05 secao 4); ausente na versao de casa.
   */
  versionId?: string
  name: string | null
  setOnTurn: number | null
  /**
   * Turnos do OPONENTE que faltam para a carta se destruir sozinha, quando ela tem prazo.
   * docs/01-regras-do-duelo.md secao 6.3
   *
   * `null` quando a carta nao tem prazo — ou quando ela esta virada para quem recebe a visao,
   * porque carta virada nem sequer foi ativada.
   */
  opponentTurnsLeft: number | null
  /**
   * `instanceId` do monstro equipado, so para Magia de Equipamento ja ativada. docs/02 secao 3.5
   *
   * Publico como a propria carta face-up: `null` tambem quando a carta esta virada (equipagem so
   * existe depois da ativacao) ou nao e um Equipamento.
   */
  equippedTo: string | null
  /**
   * O contador de turnos do procedimento, no Equipamento-monstro ja resolvido (Cocoon of Evolution): `count` sao as
   * Esperas de quem o controla desde que equipou, SEM teto; `max` e so a referencia da ficha ("x1 de 4"). Publico nas tres
   * visoes, como a carta face-up. docs/01-regras-do-duelo.md secao 4.6, ADR 0053. Opcional, so vem quando existe.
   */
  turnCounter?: TurnCounterView
}

export type HandCardView = {
  instanceId: string
  cardId: string
  /**
   * A versao da carta (ADR 0061): a arte e o acabamento da copia. Anexada pela sala onde, e so onde, o `cardId` vem
   * preenchido (docs/05 secao 4); ausente na versao de casa.
   */
  versionId?: string
}

/**
 * Uma carta numa zona PUBLICA fora do campo: cemiterio e removido do jogo.
 * docs/01-regras-do-duelo.md secao 3.0
 *
 * Traz `instanceId` porque a zona e alvo de efeito (`OPPONENT_GRAVEYARD_CHOICE`), e duas copias
 * do mesmo `cardId` precisam ser distinguiveis. Nao vaza nada: as duas zonas sao publicas.
 */
export type CardRefView = {
  instanceId: string
  cardId: string
  /**
   * A versao da carta (ADR 0061): a arte e o acabamento da copia. Anexada pela sala onde, e so onde, o `cardId` vem
   * preenchido (docs/05 secao 4); ausente na versao de casa.
   */
  versionId?: string
  /**
   * A marca de Invocacao correta, levada ao cemiterio (`properlySummoned`): com ela, a Invocacao-Especial por efeito
   * alcanca o monstro com `noNormalSummon`. Publica, como o cemiterio; a enumeracao do Reviver Monstro a le.
   * docs/01-regras-do-duelo.md secao 4.2 item 5, ADR 0053. Opcional, e so vem quando e `true`.
   */
  properlySummoned?: true
}

export type SideView = {
  playerId: string
  lifePoints: number
  terrains: Terrain[]
  monsters: (MonsterView | null)[]
  spells: (SpellView | null)[]
  fieldSpell: SpellView | null
  handCount: number
  deckCount: number
  /**
   * Composicao do deck ATIVO do duelo — so os `cardId`s, ordenados por `cardId`. Presente apenas
   * para o DONO da visao; `null` para o oponente e para o espectador.
   * docs/05-protocolo-tempo-real.md secao 4.3
   *
   * Quem esta a mesa sabe o que pos no proprio deck, e ve o que ja saiu dele. O que continua oculto
   * e a ORDEM: por isso a lista vem ordenada por `cardId` e sem `instanceId`, e nao diz qual carta
   * esta no topo.
   */
  deckContents: string[] | null
  /**
   * Quantidade no Extra Deck. Publica para os dois duelistas, e `null` para o espectador.
   * docs/05-protocolo-tempo-real.md secao 3.0.1
   *
   * A quantidade e observavel na mesa fisica — as cartas estao la, empilhadas. O CONTEUDO nao e:
   * olhar o Extra Deck alheio seria ler a mao dele com outro nome.
   */
  extraDeckCount: number | null
  /** Conteudo do Extra Deck. Presente apenas para o DONO da visao. */
  extraDeck: HandCardView[] | null
  graveyard: CardRefView[]
  /** Removido do jogo. Publico nas tres visoes, como o cemiterio. docs/01 secao 3.0 */
  banished: CardRefView[]
  /** Presente apenas para o dono da visao. */
  hand: HandCardView[] | null
  hasNormalSummoned: boolean
  /**
   * A Invocacao-Normal/Set EXTRA (`GRANT_EXTRA_NORMAL_SUMMON`) ja foi usada neste turno?
   * docs/01 secao 4.4
   *
   * Opcional (e nao irma de `hasNormalSummoned`, que e obrigatorio): so o motor preenche; um mock
   * de teste de frontend sem esta carta no jogo pode seguir sem o campo, tratado como `false`.
   */
  extraNormalSummonUsedThisTurn?: boolean
  /**
   * Algum monstro do PROPRIO campo foi enviado ao cemiterio NESTE turno — batalha ou efeito, tanto
   * faz. docs/01-regras-do-duelo.md secao 4.2 (Last Will, `leva_sdk_sdy/curadoria`)
   *
   * Publica, como `hasNormalSummoned`: e sobre o PROPRIO campo, nunca informacao oculta do
   * adversario — `legalActions` precisa dela para saber se `SELF_MONSTER_SENT_TO_GRAVEYARD_THIS_TURN`
   * libera a ativacao. Opcional pelo mesmo motivo de `extraNormalSummonUsedThisTurn`: so o motor
   * preenche, e a ausencia vale `false`.
   */
  monsterSentToGraveyardThisTurn?: boolean
  /**
   * Os `cardId`s cujo efeito "uma vez por turno pelo nome" (`ONCE_PER_TURN_BY_NAME`) este jogador ja usou NESTE turno.
   * Publico: a chegada ao cemiterio foi a vista. Ausente = nenhum. docs/01 secao 6.6, docs/05 secao 4
   */
  onceByNameUsedThisTurn?: string[]
  /**
   * Os `cardId`s buscados com a trava (`lockActivationThisTurn`): este jogador nao ativa cartas com esses NOMES pelo resto
   * do turno. Publico: a carta buscada foi mostrada. Ausente = nenhuma. docs/01 secao 6.1, docs/05 secao 4
   */
  activationLockedThisTurn?: string[]
  /**
   * Os `instanceId`s das cartas cuja ativacao este jogador desfez neste turno (a pausa da mao do oponente fechada com a
   * lista vazia, The Inexperienced Spy): aquela carta nao e ativada de novo ate o fim do turno. SO na visao do dono — o
   * `instanceId` de carta da mao nao vai a mais ninguem. Ausente = nenhuma. docs/01 secao 8, docs/05 secao 4
   */
  activationUndoneThisTurn?: string[]
}

/**
 * Janela de resposta aberta. docs/01-regras-do-duelo.md secao 6.4
 *
 * `eligibleInstanceIds` so e preenchido para quem PODE responder — sao as cartas
 * viradas dele proprio. Para o adversario a lista vem vazia: saber que a janela
 * existe e publico, saber quais cartas sao elegiveis nao e.
 */
/**
 * Opcoes de regra do duelo, como o cliente as ve. docs/01-regras-do-duelo.md secao 5.3
 *
 * E informacao PUBLICA, como o mapa de terrenos: as tres visoes a recebem igual, inclusive a do
 * espectador. Quem joga precisa saber por que regra esta jogando, e um bot que simulasse com regra
 * diferente da do servidor divergiria em silencio.
 */
export type RuleOptionsView = {
  /** Atacar Defesa com ATK menor que a DEF destroi tambem o atacante. docs/01 secao 5.3 */
  defenseDestroysAttacker: boolean
}

/**
 * Um elo da corrente, como o cliente o ve. docs/01-regras-do-duelo.md secao 6.4
 *
 * E informacao PUBLICA: por na corrente e o que revela a carta. Sem a lista, o jogador responderia
 * sem saber ao que esta respondendo — e a ordem LIFO, que e contraintuitiva para quem nao conhece o
 * jogo, seria invisivel.
 */
export type ChainLinkView = {
  controllerId: string
  instanceId: string
  cardId: string
  /**
   * A versao da carta (ADR 0061): a arte e o acabamento da copia. Anexada pela sala onde, e so onde, o `cardId` vem
   * preenchido (docs/05 secao 4); ausente na versao de casa.
   */
  versionId?: string
}

export type PendingResponseView = {
  windowId: string
  /** Identifica a corrente inteira; muda a cada corrente nova, e nao a cada elo. */
  chainId: string
  respondingPlayerId: string
  reason: EffectTrigger
  eligibleInstanceIds: string[]
  /**
   * O monstro invocado que abriu uma janela `ON_SUMMON` ou `ON_SUMMON_ATTEMPT` (a Invocacao que ainda pode ser negada,
   * docs/01 secao 4.5); `null` nas demais. PUBLICO nas tres visoes: a
   * janela so abre para invocacao com a face para cima, entao ele ja esta visivel no campo. E o alvo que a
   * tela mostra antes de o jogador responder. docs/05-protocolo-tempo-real.md secao 4
   */
  summonedInstanceId: string | null
  /**
   * O tipo da Invocacao que abriu uma janela `ON_SUMMON`; ausente nas demais. PUBLICO nas tres visoes: a Invocacao foi a
   * vista, e e o tipo que decide quem responde (`summonKinds`, docs/01 secao 6.4). docs/05-protocolo-tempo-real.md
   * secao 4
   */
  summonKind?: SummonKind
  /**
   * O ataque que abriu uma janela `ON_ATTACK_DECLARED`; `null` nas demais. PUBLICO nas tres visoes: o ataque
   * foi declarado na mesa, e atacante e alvo ja estao no campo. `targetInstanceId` `null` e ataque direto. E o
   * que deixa a tela mostrar quem ataca quem antes de o jogador responder. docs/05-protocolo-tempo-real.md secao 4
   */
  attack: { attackerInstanceId: string; targetInstanceId: string | null } | null
  /** Elos ja postos, do fundo para o topo. Resolve na ordem INVERSA. */
  links: ChainLinkView[]
}

/**
 * De onde sai o alvo de um passo de escolha. docs/02-catalogo-de-cartas.md secao 3.2
 *
 * E o subconjunto dos alvos `_CHOICE` que a escolha de virada sabe pedir. Alvo de escolha fora
 * desta lista (mao, cemiterio, jogador) mantem o criterio automatico de 01 secao 6.6 — a primeira
 * carta de virada que precisar dele amplia a lista.
 */
export const CHOICE_STEP_SOURCES = [
  'SELF_MONSTER',
  'OPPONENT_MONSTER',
  'ANY_MONSTER',
  'ANY_FACE_UP_MONSTER',
  'ANY_SPELL',
  'ANY_FACE_UP_SPELL',
  /**
   * O PROPRIO Deck, na escolha PRE-ativacao de `SELF_DECK_CHOICE` (Last Will,
   * `leva_sdk_sdy/curadoria`). docs/05-protocolo-tempo-real.md secao 4
   *
   * Com `SELF_DECK_TOP`, os unicos passos cujos candidatos trazem `cardId` — ver `ChoiceStepView.candidateCardIds` — porque
   * o Deck do dono e sempre oculto (diferente de monstro/magia em campo, cujo `instanceId` ja e
   * publico mesmo virado): sem o `cardId`, quem escolhe nao saberia o que esta escolhendo.
   */
  'DECK',
  /**
   * A MAO DO OPONENTE, na escolha PRE-ativacao de `OPPONENT_HAND_CHOICE` (The Inexperienced Spy,
   * `leva_sdk_sdy/curadoria`). docs/05-protocolo-tempo-real.md secao 4
   *
   * Diferente de `DECK`: os candidatos aqui NUNCA trazem `candidateCardIds` — a mao do oponente e
   * oculta como o Deck, mas a escolha e ÀS CEGAS por natureza (o jogador aponta a posicao sem
   * saber o que ha nela, e descobrir e o efeito da carta). Fora da janela de escolha, a mao do
   * oponente continua `null` em toda `PlayerView` (docs/05 secao 4) — so os `instanceId` deste
   * passo, e so para quem escolhe, ficam visiveis por instante.
   */
  'OPPONENT_HAND',
  /**
   * O PROPRIO cemiterio (`SELF_GRAVEYARD_CHOICE`, Magician of Faith). Publico: os candidatos sao cartas que todos ja
   * veem. docs/01-regras-do-duelo.md secao 6.6
   */
  'SELF_GRAVEYARD',
  /**
   * O TOPO do proprio Deck, para por na ordem escolhida (`SELF_DECK_TOP_CHOICE`, Big Eye). Como `DECK`, os candidatos
   * trazem `candidateCardIds`, so para quem escolhe, e a escolha e a permutacao inteira deles. docs/05 secao 4.2
   */
  'SELF_DECK_TOP',
  /**
   * A PROPRIA mao, para o fundo do Deck (`SELF_HAND_CHOICE` com `anyNumber`, Yado Karu). O dono ja ve a mao, e por
   * isso sem `candidateCardIds`. Vem com `atMost`. docs/01-regras-do-duelo.md secao 6.6
   */
  'SELF_HAND',
  /**
   * A PROPRIA mao e o PROPRIO Deck numa lista so, na escolha PRE-ativacao de `SELF_HAND_OR_DECK_CHOICE` (Elegant
   * Egotist, ADR 0053). Como `DECK`, os candidatos trazem `candidateCardIds`, so para quem escolhe, na ordem do criterio
   * automatico: maior ATK base, empate pelo menor `cardId`, e na mesma carta a da mao primeiro. docs/05 secao 4.2
   */
  'HAND_OR_DECK',
] as const
export type ChoiceStepSource = (typeof CHOICE_STEP_SOURCES)[number]

/**
 * Um passo da escolha de alvo: quantos alvos, de onde, e quais servem.
 * docs/01-regras-do-duelo.md secao 6.6
 *
 * `candidateInstanceIds` so vem preenchido para QUEM ESCOLHE (docs/05 secao 4): a lista alcanca
 * carta virada para baixo dos dois lados, e ela nunca traz `cardId` — quem escolhe aponta a zona, e
 * descobrir o que estava ali e o que a carta faz.
 */
export type ChoiceStepView = {
  source: ChoiceStepSource
  count: number
  candidateInstanceIds: string[]
  /**
   * "De 1 a `count`", e nao exato (docs/01-regras-do-duelo.md secao 6.6, Yado Karu). So no ULTIMO passo: a lista de
   * `targets` e plana. A lista vazia continua sendo o criterio automatico. Publico, como `count`.
   */
  atMost?: true
  /**
   * SO nos passos `source: 'DECK'`, `'HAND_OR_DECK'` (Elegant Egotist) e `'SELF_DECK_TOP'` (Big Eye), e SO na view de
   * quem escolhe. docs/05-protocolo-tempo-real.md secao 4 (Last Will, `leva_sdk_sdy/curadoria`)
   *
   * Paralelo a `candidateInstanceIds`, mesmo indice: o Deck do dono e sempre oculto, entao —
   * diferente de todo outro passo, onde a identidade da carta e o que o EFEITO revela — aqui o
   * `cardId` precisa vir JUNTO para a escolha fazer sentido. Ja vem filtrado pelo criterio da
   * carta (`atkAtMost`) no instante em que a escolha abriu; o oponente e o espectador NUNCA
   * recebem este campo, nem vazio — o passo deles vem com `candidateInstanceIds: []` e sem
   * `candidateCardIds`, como qualquer outro passo que nao e deles.
   */
  candidateCardIds?: string[]
}

/**
 * O duelo esta PARADO esperando o jogador escolher o alvo de um efeito de virada.
 * docs/01-regras-do-duelo.md secao 6.6
 *
 * Quem escolhe e o controlador do monstro virado, mesmo fora do turno dele. A carta que virou ja
 * esta face-up, e por isso `sourceCardId` e publico.
 */
export type PendingChoiceView = {
  choiceId: string
  choosingPlayerId: string
  /**
   * `sourceInstanceId` e `sourceCardId` vem `null`, e `steps` vazio, so na pausa antes da ativacao da mao do oponente
   * (The Inexperienced Spy), para quem NAO escolhe: a carta ainda esta na mao ou virada na zona, a ativacao pode ser
   * desfeita, e o passo `OPPONENT_HAND`, exclusivo do Spy, ou a instancia apontariam a carta. Se ela segue, o
   * `SPELL_ACTIVATED` a revela. docs/05-protocolo-tempo-real.md secao 4
   */
  sourceInstanceId: string | null
  sourceCardId: string | null
  steps: ChoiceStepView[]
  /**
   * A escolha e do OPONENTE de quem ativou uma Magia Normal, antes de o elo dela resolver (Share the Pain): quem
   * escolhe oferece 1 monstro proprio como Tributo. Publico nas tres visoes; ausente nas outras pausas.
   * docs/05-protocolo-tempo-real.md secao 4, ADR 0049
   */
  resolvingLink?: true
  /**
   * A escolha e a busca no Deck da carta que chegou ao cemiterio vinda do campo (`ON_SENT_TO_GRAVEYARD`, Witch of the
   * Black Forest e Sangan): quem escolhe e o dono, e a carta de origem esta no cemiterio. Publico nas tres visoes;
   * ausente nas outras pausas. docs/05-protocolo-tempo-real.md secao 4, ADR 0051
   */
  graveyardArrival?: true
}

export type PlayerView = {
  duelId: string
  turn: number
  phase: Phase
  /** Fases visitadas NESTE turno, na ordem em que ocorreram. Fase pulada nunca aparece aqui,
   * mesmo com ordem menor que a atual. docs/18-frontend-web2.md secao 7. */
  visitedPhasesThisTurn: Phase[]
  activePlayerId: string
  status: DuelStatus
  winnerId: string | null
  endReason: DuelEndReason | null
  /** Terreno imposto pela Magia de Campo que esta VALENDO; `null` = vale o mapa das zonas. */
  activeFieldTerrain: Terrain | null
  /**
   * De quem e a Magia de Campo que esta valendo. docs/01-regras-do-duelo.md secao 3.3
   *
   * Cada lado tem a propria (`SideView.fieldSpell`), e as duas podem estar ocupadas: vale a mais
   * recente, e a outra fica em campo INERTE. Sem este campo a tela nao teria como dizer qual e
   * qual — e uma carta que nao pinta nada e nao avisa e o defeito mais provavel desta regra.
   */
  activeFieldControllerId: string | null
  /**
   * A Reverse Trap esta invertendo os modificadores e o bonus consultado ate o fim do turno. docs/01 secao 3.4
   *
   * Publico nas tres visoes: a ativacao foi a vista. Sem ele, quem reconstroi o valor efetivo (o bot) nao tem como
   * separar o modificador do bonus. Opcional, e so vem quando e `true`.
   */
  atkDefModifiersReversed?: true
  /**
   * O `END_TURN` terminou a Fase Final, e o turno so vira quando a busca na chegada ao cemiterio aberta nela (e a fila)
   * fecharem. Publico nas tres visoes; ausente fora disso. docs/01 secao 6.6, docs/05 secao 4, ADR 0051
   */
  turnEndPending?: true
  /**
   * A Final parada: o jogador do turno esta na Final com uma habilidade de Final legal (Mushroom Man #2), e ela termina
   * com o `END_TURN` ou sozinha, depois de usada. Publico nas tres visoes; ausente fora disso. docs/01 secao 2,
   * docs/05 secao 4, ADR 0052
   */
  endPhaseStopped?: true
  /** Opcoes de regra do duelo. Publicas. docs/01-regras-do-duelo.md secao 5.3 */
  rules: RuleOptionsView
  /**
   * Modo de terreno do duelo. Publico. docs/01-regras-do-duelo.md secao 3
   *
   * A tela o mostra nos TRES modos, e nao so nos dois novos: exibi-lo apenas quando e "diferente"
   * faria a ausencia virar informacao (docs/18 secao 23.2).
   */
  terrainMode: TerrainMode
  /**
   * As respostas livres de quem ve estao desligadas. So na visao do dono, e so quando desligadas: ao oponente diria que
   * ele tem carta livre baixada. docs/01 secao 6.4, docs/05 secao 4, docs/backlog/165
   */
  freeResponsesOff?: true
  /** Quando preenchido, o duelo esta parado esperando a resposta de um jogador. */
  pendingResponse: PendingResponseView | null
  /** Quando preenchido, o duelo esta parado esperando a escolha de alvo de um efeito de virada. */
  pendingChoice: PendingChoiceView | null
  you: SideView
  opponent: SideView
  /**
   * `GRANT_MANDATORY_TRIBUTE` (Soul Exchange): `instanceId` do monstro designado como Tributo
   * obrigatorio pelo resto do turno, e `mandatoryTributeOwnerId` — o dono dele. Ausentes fora
   * dessa restricao. docs/01-regras-do-duelo.md secao 4, docs/02-catalogo-de-cartas.md secao 3.3
   *
   * PUBLICOS nas tres visoes: a restricao e sobre uma jogada (Invocacao por Tributo), nao sobre
   * carta oculta. `legalActions` (`packages/engine`) precisa deles para enumerar Invocacao por
   * Tributo sem divergir do reducer.
   */
  mandatoryTributeInstanceId?: string
  mandatoryTributeOwnerId?: string
  /**
   * `GRANT_MANDATORY_TRIBUTE` (Soul Exchange, docs/01 secao 5.1): o jogador do turno nao tem Fase de Batalha neste
   * turno. Ausente fora da restricao. PUBLICO nas tres visoes, como o Tributo designado: a ativacao foi a vista. O bot
   * precisa dele para reconstruir o turno como o motor real (docs/07 secao 2.3.6).
   */
  battlePhaseSkippedThisTurn?: true
}

/**
 * O que um ESPECTADOR pode ver. docs/05-protocolo-tempo-real.md secao 4.
 *
 * E a mais restrita das tres visoes, e nao um `PlayerView` de terceiro: os dois lados vem com
 * `hand: null`, e carta virada vem sem `cardId` **dos dois lados**. Um espectador nunca sabe algo
 * que um dos duelistas nao sabe.
 *
 * Nao ha `you` nem `opponent` de proposito. Espectador nao tem lado, e nomear um deles "you" seria
 * um nome que mente — e mentir no nome do campo e como alguem passa a visao errada para a tela. Quem
 * desenha escolhe de qual ponta olhar, do mesmo jeito que o visualizador de replay ja faz.
 */
export type SpectatorView = {
  duelId: string
  turn: number
  phase: Phase
  /** Fases visitadas NESTE turno. Ver `PlayerView.visitedPhasesThisTurn`. */
  visitedPhasesThisTurn: Phase[]
  activePlayerId: string
  status: DuelStatus
  winnerId: string | null
  endReason: DuelEndReason | null
  activeFieldTerrain: Terrain | null
  /** De quem e a Magia de Campo que esta valendo. docs/01 secao 3.3 */
  activeFieldControllerId: string | null
  /** Ver `PlayerView.turnEndPending` — mesma visao publica. */
  turnEndPending?: true
  /** Ver `PlayerView.endPhaseStopped` — mesma visao publica. */
  endPhaseStopped?: true
  /** Publicas, como para os duelistas: por que regra o duelo corre nao e informacao oculta. */
  rules: RuleOptionsView
  /** Publico, como para os duelistas. docs/01-regras-do-duelo.md secao 3 */
  terrainMode: TerrainMode
  /** A lista de elegiveis vem **sempre vazia**: ela diria quais cartas viradas podem responder. */
  pendingResponse: PendingResponseView | null
  /** Como para o oponente de quem escolhe: sem os candidatos de cada passo. docs/05 secao 4 */
  pendingChoice: PendingChoiceView | null
  /** Os dois lados, na ordem dos assentos do duelo. */
  sides: [SideView, SideView]
  /** Ver `PlayerView.mandatoryTributeInstanceId`/`mandatoryTributeOwnerId` — mesma visao publica. */
  mandatoryTributeInstanceId?: string
  mandatoryTributeOwnerId?: string
  /** Ver `PlayerView.battlePhaseSkippedThisTurn` — mesma visao publica. */
  battlePhaseSkippedThisTurn?: true
}

// ---------------------------------------------------------------------------
// Erros
// ---------------------------------------------------------------------------

export const DUEL_ERROR_CODES = [
  'NOT_YOUR_TURN',
  'WRONG_PHASE',
  'ILLEGAL_ACTION',
  'UNKNOWN_CARD',
  'INVALID_TARGET',
  'DUEL_FINISHED',
  'RATE_LIMITED',
  'BAD_SCHEMA',
] as const
export type DuelErrorCode = (typeof DUEL_ERROR_CODES)[number]

export type ActionFailure = { ok: false; code: DuelErrorCode; message: string }

export const terrainSchema = z.enum(TERRAINS)

export const terrainModeSchema = z.enum(TERRAIN_MODES)

// ---------------------------------------------------------------------------
// Sumario do duelo e o envelope do fim. docs/18-frontend-web2.md secao 14
// ---------------------------------------------------------------------------

/**
 * Invocacoes por tipo, uma contagem por TIPO DE EVENTO. docs/01-regras-do-duelo.md secao 4
 *
 * Nenhuma categoria e inferida: cada uma sai de um evento, e nao de deducao sobre o que aconteceu
 * ao redor. **A Invocacao por Tributo nao aparece** porque `MONSTER_SUMMONED` nao carrega os
 * tributos pagos — ela entra em `normal`, e a pendencia esta registrada em docs/18 secao 13.
 */
export const duelSummarySummonsSchema = z.object({
  normal: z.number().int().nonnegative(),
  set: z.number().int().nonnegative(),
  flip: z.number().int().nonnegative(),
  fusion: z.number().int().nonnegative(),
  ritual: z.number().int().nonnegative(),
  special: z.number().int().nonnegative(),
})
export type DuelSummarySummons = z.infer<typeof duelSummarySummonsSchema>

/**
 * O que um jogador fez no duelo.
 *
 * `revealedCardIds` so recebe carta que ja era **publica para os dois lados**: o acumulador roda no
 * servidor, sobre eventos ainda nao filtrados pela fog of war, e por isso a protecao mora nele e
 * nao na tela (docs/05 secao 4.1). Contagem agregada nao revela identidade e continua valendo —
 * `cardsDrawn` conta compras sem dizer quais.
 */
export const duelSummarySideSchema = z.object({
  playerId: z.string().min(1),
  /** LP no fim do duelo. Pode ser negativo: quem terminou negativo perdeu. */
  lifePoints: z.number().int(),
  damageDealt: z.number().int().nonnegative(),
  damageTaken: z.number().int().nonnegative(),
  /** O maior golpe unico recebido pelo OUTRO lado — o melhor ataque deste jogador. */
  biggestHit: z.number().int().nonnegative(),
  attacksDeclared: z.number().int().nonnegative(),
  battlesWon: z.number().int().nonnegative(),
  monstersLost: z.number().int().nonnegative(),
  cardsDrawn: z.number().int().nonnegative(),
  cardsSentToGraveyard: z.number().int().nonnegative(),
  /** Magia e Armadilha num numero so: separar exigiria o catalogo, que nao entra no motor. */
  activations: z.number().int().nonnegative(),
  summons: duelSummarySummonsSchema,
  revealedCardIds: z.array(z.string().min(1)),
})
export type DuelSummarySide = z.infer<typeof duelSummarySideSchema>

/**
 * O duelo inteiro, contado. docs/18-frontend-web2.md secao 14
 *
 * **Uma entrada por jogador, e nao um par "voce/oponente".** Quem decide o lado e o cliente,
 * comparando `playerId` com o seu — e e isso que faz o MESMO payload servir duelista e espectador
 * (docs/05 secao 4), sem uma segunda projecao.
 *
 * **Nenhum nome de carta atravessa daqui.** So `cardId`: nome, tipo e arte saem do catalogo, no
 * cliente (docs/99-aviso-legal.md).
 */
export const duelSummarySchema = z.object({
  turns: z.number().int().nonnegative(),
  /** Na ordem dos assentos do duelo, como `SpectatorView.sides`. */
  sides: z.array(duelSummarySideSchema),
})
export type DuelSummary = z.infer<typeof duelSummarySchema>

/**
 * A mensagem `DUEL_END`. docs/05-protocolo-tempo-real.md secao 3
 *
 * Ela existia sem contrato: a forma vivia duplicada no `endPayload()` do `game-server` e na
 * declaracao a mao do cliente, e nada obrigava as duas a concordar. E por isso que `reason` chegava
 * como `string` e virava `DuelEndReason` por um `as` — uma promessa que ninguem verificava.
 *
 * `summary` e `durationSeconds` sao ANULAVEIS de proposito: uma sala no ar antes do sumario existir
 * precisa continuar terminando o duelo, e a tela degrada em vez de quebrar.
 *
 * `rewards` e sempre `null`, e continua sendo: a sala nao escreve no banco (ADR 0004), e DP, XP e
 * carta sao assunto do `api`.
 */
export const duelEndMessageSchema = z.object({
  winnerId: z.string().min(1).nullable(),
  reason: z.enum(DUEL_END_REASONS).nullable(),
  rewards: z.null(),
  summary: duelSummarySchema.nullable(),
  /** Medido pelo relogio do SERVIDOR, e congelado no instante do fim. */
  durationSeconds: z.number().nonnegative().nullable(),
})
export type DuelEndMessage = z.infer<typeof duelEndMessageSchema>

/**
 * O nome de um duelista, como o protocolo o carrega. docs/05-protocolo-tempo-real.md secao 3.0.2
 *
 * ANULAVEL sempre: o nome nasce no `api` (ADR 0004) e a sala so o repassa, entao ela nao tem como
 * garanti-lo: sala de simulacao nao tem conta por tras, e conta apagada nao tem mais nome. O
 * cliente cai no rotulo generico dele, nunca no `playerId`: id cru nao e nome (docs/08 secao 10), e
 * o do adversario e identificador de terceiro.
 */
const duelistNameSchema = z.string().min(1).nullable().default(null)

/**
 * O personagem de um duelista (ADR 0043, docs/05 secao 3.0.3): o id do catalogo, e nao o caminho da
 * imagem. ANULAVEL sempre: `null` e "sem personagem" (desligado, desconhecido, bot sem personagem),
 * e a tela desenha a moldura vazia. Ausente vale `null`: sala publicada antes desta decisao nao o
 * manda, e o cliente cai na reserva em vez de recusar o payload.
 */
const duelistCharacterSchema = z.string().min(1).nullable().default(null)

/**
 * A moldura do painel de um duelista (docs/06 secao 6.2, docs/05 secao 3.0.4): o id do catalogo.
 * ANULAVEL sempre, e `null` e a Padrao (desligada, desconhecida, bot). Ausente vale `null`, pelo mesmo
 * motivo do personagem.
 */
const duelistFrameSchema = z.string().min(1).nullable().default(null)

/**
 * A mensagem `DUEL_START`, na variante do DUELISTA. docs/05-protocolo-tempo-real.md secao 3
 *
 * Ela existia sem contrato, como `DUEL_END` antes de docs/18 secao 14: a forma vivia no
 * `game-server` e numa declaracao a mao do cliente, sem nada obrigando as duas a concordar. Ganhou
 * contrato quando passou a carregar informacao que a tela usa: o nome dos duelistas.
 *
 * **O nome nao esta em `SideView`, e nao vai estar.** A visao e projetada pelo motor, que e puro e
 * nao conhece conta (ADR 0001); nome e dado de perfil. Aqui ele viaja uma vez, na entrada, e nao a
 * cada `STATE_SYNC`: nome nao muda no meio do duelo.
 */
export const duelStartMessageSchema = z.object({
  duelId: z.string().min(1),
  you: z.string().min(1),
  opponent: z.string().min(1),
  youName: duelistNameSchema,
  opponentName: duelistNameSchema,
  youCharacter: duelistCharacterSchema,
  opponentCharacter: duelistCharacterSchema,
  youFrame: duelistFrameSchema,
  opponentFrame: duelistFrameSchema,
  seed: z.number(),
  contentVersion: z.string(),
  terrainMap: z.array(terrainSchema),
  startingPlayerId: z.string().min(1),
})
export type DuelStartMessage = z.infer<typeof duelStartMessageSchema>

/**
 * Quanto dura a apresentacao, antes da escolha de jogar primeiro. docs/05-protocolo-tempo-real.md secao 3.0
 *
 * E a duracao da tela de VS (docs/18 secao 15.2): o chamado "E hora do duelo!" (3,306 s, medido do arquivo) com
 * folga, porque a tela nao escuta o fim do som e o audio pode demorar a comecar. Mora no contrato porque os dois
 * lados a usam: o `game-server` segura a escolha por ela, e o `web2` desenha a tela por ela. Trocar o chamado pede
 * medir o novo e conferir que este numero ainda o cobre.
 */
export const DUEL_INTRO_MS = 4500

/**
 * O teto da espera pelos dois duelistas, antes da apresentacao. docs/05-protocolo-tempo-real.md secao 3.0
 *
 * A apresentacao so comeca com todos os duelistas humanos na sala, e quem nao vem nao pode trava-la. Contado desde
 * que a sala nasce: estourado, ela comeca com quem estiver, e o ausente segue o fluxo de sempre.
 */
export const DUEL_JOIN_WAIT_MS = 10_000

/**
 * A apresentacao: quem vai duelar, antes do sorteio. docs/05-protocolo-tempo-real.md secao 3.0
 *
 * So o que a tela de VS desenha, do ponto de vista de quem recebe, e nada do duelo: as maos ainda nao foram
 * compradas, e o jogador inicial ainda nao existe. Os nomes e personagens seguem as regras do `DUEL_START`,
 * que repete os mesmos valores depois da escolha.
 */
export const duelIntroMessageSchema = z.object({
  youName: duelistNameSchema,
  opponentName: duelistNameSchema,
  youCharacter: duelistCharacterSchema,
  opponentCharacter: duelistCharacterSchema,
  /** `true` enquanto falta duelista na sala, e a tela de VS espera; `false` no confronto, com o chamado. */
  waiting: z.boolean(),
})
export type DuelIntroMessage = z.infer<typeof duelIntroMessageSchema>

/**
 * A mesma mensagem, na variante da PLATEIA. docs/05-protocolo-tempo-real.md secao 4
 *
 * Nao ha `you` nem `youName`: espectador nao tem lado, e nomear um assento de "voce" para quem nao
 * duela seria um nome que mente. Os dois nomes vem em `names`, paralelo a `sides` e na mesma ordem
 * dos assentos, do mesmo jeito que `sides` ja resolve a mesma pergunta para os ids.
 */
export const spectatorDuelStartMessageSchema = z.object({
  duelId: z.string().min(1),
  spectator: z.literal(true),
  sides: z.tuple([z.string().min(1), z.string().min(1)]),
  names: z.tuple([duelistNameSchema, duelistNameSchema]).default([null, null]),
  /** Os personagens, na mesma ordem e sob a mesma regra de `names`: nome escondido, personagem tambem. */
  characters: z.tuple([duelistCharacterSchema, duelistCharacterSchema]).default([null, null]),
  seed: z.number(),
  contentVersion: z.string(),
  terrains: z.array(z.array(terrainSchema)),
})
export type SpectatorDuelStartMessage = z.infer<typeof spectatorDuelStartMessageSchema>
